import { defineNuxtRouteMiddleware, navigateTo } from '#imports'
import { useAmazonState } from '~/composables/useAmazonState'
import type { RouteLocationNormalized } from 'vue-router'

// List of paths that should be excluded from Amazon prefixing
// Add any paths that should never have the /amazon prefix here
const EXCLUDED_PATHS = [
  '/login',
  '/logout',
  '/amazon', // Already has the prefix
  '/api/', // API routes
  '/_nuxt/', // Nuxt internal routes
  '/page-not-found',
]

// Check if a path should be excluded from Amazon prefixing
const shouldExcludePath = (path: string): boolean => {
  return EXCLUDED_PATHS.some((excludedPath) => path.startsWith(excludedPath))
}

// Check if a path is a product detail page
const isProductDetailPage = (path: string): boolean => {
  // Match paths like /products/12345 but not /products/list
  return /^\/products\/(?!list)[^/]+/.test(path)
}

export default defineNuxtRouteMiddleware(
  (to: RouteLocationNormalized, _from: RouteLocationNormalized) => {
    try {
      const { setIsAmazon, isAmazon } = useAmazonState()
      const isAmazonContext = isAmazon()

      // Check if the route has the Amazon prefix and set the state
      if (to.path.startsWith('/amazon')) {
        //setIsAmazon(true)

        // If the path is exactly /amazon or /amazon/, redirect to a default Amazon landing page
        /*if (to.path === '/amazon' || to.path === '/amazon/') {
          return navigateTo('/amazon/eyebot-welcome')
        }*/
        return navigateTo('/page-not-found')
      } else if (!_from.path.startsWith('/amazon')) {
        // Only clear Amazon state if we're not on an Amazon route
        // and we're not coming from an Amazon route
        setIsAmazon(false)
        return
      }

      // Special handling for product detail pages
      if (isProductDetailPage(to.path) && isAmazonContext) {
        // Convert /products/12345 to /amazon/products/12345
        const newPath = to.path.replace(/^\/products\//, '/amazon/products/')
        return navigateTo({
          path: newPath,
          query: to.query,
          hash: to.hash,
        })
      }

      // If we're in Amazon context and the target route doesn't have the /amazon prefix
      // and it's not in the excluded paths, redirect to the same route with /amazon prefix
      if (
        isAmazonContext &&
        !to.path.startsWith('/amazon') &&
        !shouldExcludePath(to.path)
      ) {
        // Construct the new path with the /amazon prefix
        const newPath = `/amazon${to.path}`

        // Preserve query parameters and hash
        return navigateTo({
          path: newPath,
          query: to.query,
          hash: to.hash,
        })
      }
    } catch (error) {
      console.error('Error in Amazon route middleware:', error)
      // Fallback to a safe route in case of errors
      return navigateTo('/amazon/eyebot-welcome')
    }
  },
)
